import React, { useRef, useState } from "react";
import { Box, Fab, useMediaQuery, useTheme } from "@mui/material";
import { FaHandPointUp, FaAngleLeft, FaAngleRight } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { RootState } from "../../redux/store";
import { InstitucionalPalette, themePalette } from "../../config/theme.config";
import { HeaderBar } from "../Header";
import { Footer } from "../Footer";
import { SidebarIcons } from "./SideBarIcons";
import { HeaderLogIn } from "../HeaderLogIn";
import AccountService from "../../services/AccountService";
import { useCookies } from "react-cookie";
import { SetLoggedUser } from "../../types/Usuario/LoggedUser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { inUser, outUser } from "../../redux/slices/auth.slice";
import { decodeToken } from "../../utils/jwtUtils";

interface LayoutProps {
    children: React.ReactNode;
}

export const LayoutNotAuth: React.FC<LayoutProps> = ({ children }) => {
    const theme = useTheme();
    const user = useSelector((state: RootState) => state.authReducer.userData);

    const [cookies, setCookie] = useCookies(["tokenEspolAds"]);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const { accounts } = useMsal();
    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const headerRef = useRef<HTMLDivElement>(null);
    const isXSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

    const [headerHeight, setHeaderHeight] = useState(0);
    const [isTestLogin, setIsTestLogin] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const sidebarWidth = 180;
    const collapsedWidth = 40;

    const getMinHeight = () => {
        if (isXSmallScreen) return "50vh";
        if (isSmallScreen) return "60vh";
        if (isMediumScreen) return "80vh";
        return "95vh";
    };

    const handleScrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleSidebar = () => setSidebarOpen((prev) => !prev);

    const minHeight = getMinHeight();

    async function GetUser(usuario: string) {
        await AccountService.LoginEspolAds(usuario)
            .then((data) => {
                //console.log(data)
                var person = SetLoggedUser(data);
                dispatch(inUser({
                    userData: person,
                    token: data.token,
                    isAuth: true,
                    isLoading: false
                })); // Guarda en Redux
                setCookie("tokenEspolAds", data.token, { path: "/", secure: false, sameSite: "strict" });
            }).catch((error) => {
                console.error(error)
            })
            .finally(() => {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);;
            });
    }

    const logout = () => {
        setCookie("tokenEspolAds", "", { path: "/", expires: new Date(0) });
        dispatch(outUser());
        instance.logoutRedirect({
            postLogoutRedirectUri: "/",
        });
    }

    React.useEffect(() => {
        if (headerRef.current) {
            setHeaderHeight(headerRef.current.clientHeight);
        }

        // por si cambia el tamaño de la ventana
        const handleResize = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.clientHeight);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    React.useEffect(() => {
        if (!isAuthenticated || accounts.length === 0) {
            console.warn("Usuario no autenticado");
            setLoading(false);
            return;
        }

        const token = cookies.tokenEspolAds;
        if (!token) {
            // Si no hay token, pero sí cuentas, obtener usuario por accounts
            if (accounts[0]?.username) {
                GetUser(accounts[0].username.split('@')[0]);
            }
            setLoading(false);
            return;
        }

        const decodedToken = decodeToken(token);
        //console.log(decodedToken)
        dispatch(inUser({
            userData: SetLoggedUser(decodedToken, token),
            token,
            isAuth: true,
            isLoading: false
        }));

        if (decodedToken?.exp) {
            const expTime = decodedToken.exp * 1000;
            const timeUntilExpiration = expTime - Date.now();
            if (timeUntilExpiration > 0) {
                setTimeout(() => {
                    console.warn("Token Expirado");
                    logout();
                }, timeUntilExpiration);
            } else {
                logout();
            }
        } else if (accounts[0]?.username) {
            GetUser(accounts[0].username.split('@')[0]);
        }

        setLoading(false);
    }, [isAuthenticated, accounts, cookies.tokenEspolAds]);

    React.useLayoutEffect(() => {
        const measureHeader = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.clientHeight);
            }
        };

        // Medición inicial
        measureHeader();

        // Recalcular si cambia el tamaño de la ventana
        window.addEventListener("resize", measureHeader);
        return () => window.removeEventListener("resize", measureHeader);
    }, [user]);

    return (
        <Box sx={{ minHeight: "100vh" }}>
            <Box ref={headerRef}
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    zIndex: 1100,
                    borderBottom: `2px solid ${themePalette.NAVCOLOR}`,
                }}
            >
                {user.usuario.length > 0 ? <HeaderLogIn user={user} foto={''} isTest={isTestLogin} /> : <HeaderBar isTest={isTestLogin} />}
            </Box>
            <motion.div
                animate={{ width: sidebarOpen ? sidebarWidth : collapsedWidth }}
                transition={{ duration: 0.3 }}
                style={{
                    position: "fixed",
                    top: headerHeight, // altura del header
                    left: 0,
                    bottom: 0,
                    background: InstitucionalPalette.PRIMARY,
                    borderRight: "1px solid #ddd",
                    borderTopRightRadius: "16px",
                    borderBottomRightRadius: "16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    overflow: "hidden",
                    zIndex: 1200,
                }}
            >
                {sidebarOpen && (
                    <Box sx={{ p: 2 }}>
                        <SidebarIcons isAuth={user.usuario.length > 0} userRoles={user.roles} />
                    </Box>
                )}
            </motion.div>
            <Box
                sx={{
                    position: "fixed",
                    top: "50%",
                    left: sidebarOpen ? sidebarWidth - 15 : collapsedWidth - 15,
                    transform: "translateY(-50%)",
                    bgcolor: "primary.main",
                    color: "white",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: 2,
                    zIndex: 1301,
                }}
                onClick={toggleSidebar}
            >
                {sidebarOpen ? <FaAngleLeft /> : <FaAngleRight />}
            </Box>
            <Box
                component="main"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    mt: 15,
                    mb: 2,
                    minHeight: minHeight,
                    p: 2,
                }}
            >
                {children}
                <Footer />
            </Box>
            <Fab
                color="primary"
                size="small"
                onClick={handleScrollToTop}
                sx={{
                    position: "fixed",
                    bottom: 16,
                    right: 16,
                    zIndex: 1200,
                }}
            >
                <FaHandPointUp />
            </Fab>
        </Box>
    );
};
